import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Input, Tabs, Grid, StatCard, Alert } from '../../ui/components';
import { RefreshCw, Eye, FileCheck, TrendingUp, AlertTriangle } from 'lucide-react';
import { authService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

// Underwriter configuration (would come from logged-in user profile)
const UNDERWRITER_CONFIG = {
  name: 'Sarah Mitchell',
  email: 'sarah.mitchell@loanknow.com',
  capacity: 25,
  products: ['Conventional', 'FHA', 'VA'], // Products this underwriter can handle
};

const getStatusColor = (status) => {
  switch (status) {
    case 'submitted_for_underwriting':
      return 'warning';
    case 'underwriting_in_progress':
      return 'primary';
    case 'conditionally_cleared':
      return 'success';
    case 'cleared_for_marketplace':
      return 'success';
    case 'needs_work':
      return 'danger';
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'submitted_for_underwriting':
      return 'Pending Review';
    case 'underwriting_in_progress':
      return 'In Progress';
    case 'conditionally_cleared':
      return 'Cond. Cleared';
    case 'cleared_for_marketplace':
      return 'Cleared';
    case 'needs_work':
      return 'Needs Work';
    default:
      return status;
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'primary';
  if (score >= 40) return 'warning';
  return 'danger';
};

const UnderwriterDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [pipeline, setPipeline] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch real data from Supabase
  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all applications that need underwriting or are in progress
      const { data: apps, error: appsError } = await supabase
        .from('loan_applications')
        .select(`
          *,
          recommended_product:loan_products!recommended_product_id(name, short_name),
          selected_product_data:loan_products!selected_product_id(name, short_name)
        `)
        .in('status', ['submitted_for_underwriting', 'underwriting_in_progress', 'conditionally_cleared', 'cleared_for_marketplace'])
        .order('created_at', { ascending: false });

      if (appsError) {
        console.warn('Supabase fetch failed, using mock data:', appsError);
        // Use mock data for demo purposes
        const mockApps = [
          {
            id: 'demo-001',
            anonymous_id: 'DEMO001',
            borrower_info: { name: 'John Smith', email: 'john@example.com' },
            product_type: 'Conventional',
            loan_amount: 350000,
            readiness_score: 82,
            status: 'underwriting_in_progress',
            created_at: new Date().toISOString(),
            property_type: 'Single Family',
            credit_score: 740,
            income_monthly: 8500,
          },
          {
            id: 'demo-002',
            anonymous_id: 'DEMO002',
            borrower_info: { name: 'Sarah Johnson', email: 'sarah@example.com' },
            product_type: 'FHA',
            loan_amount: 275000,
            readiness_score: 75,
            status: 'submitted_for_underwriting',
            created_at: new Date().toISOString(),
            property_type: 'Condo',
            credit_score: 680,
            income_monthly: 6200,
          },
          {
            id: 'demo-003',
            anonymous_id: 'DEMO003',
            borrower_info: { name: 'Mike Davis', email: 'mike@example.com' },
            product_type: 'VA',
            loan_amount: 425000,
            readiness_score: 88,
            status: 'conditionally_cleared',
            created_at: new Date().toISOString(),
            property_type: 'Single Family',
            credit_score: 760,
            income_monthly: 9800,
          },
        ];
        
        const transformedApps = mockApps.map(app => ({
          id: app.id,
          anonymous_id: app.anonymous_id,
          applicant: {
            first_name: app.borrower_info?.name?.split(' ')[0] || 'Unknown',
            last_name: app.borrower_info?.name?.split(' ').slice(1).join(' ') || '',
            email: app.borrower_info?.email || '',
            is_test: true,
          },
          selected_product: {
            name: app.product_type || 'Conventional',
            short_name: app.product_type?.split(' ')[0] || 'Conv',
          },
          loan_amount: parseFloat(app.loan_amount) || 0,
          readiness_score: app.readiness_score || 0,
          status: app.status,
          submitted_at: app.created_at,
          property_type: app.property_type || 'Single Family',
          credit_score: app.credit_score || 0,
          dti_ratio: app.income_monthly ? ((app.loan_amount * 0.006) / app.income_monthly * 100).toFixed(1) : 0,
          assigned_underwriter_id: app.status === 'underwriting_in_progress' ? 'current-user' : null,
        }));

        const myPipeline = transformedApps.filter(app => 
          app.status === 'underwriting_in_progress' || 
          app.status === 'conditionally_cleared' ||
          app.status === 'cleared_for_marketplace'
        );

        const unassignedApps = transformedApps.filter(app => 
          app.status === 'submitted_for_underwriting' && !app.assigned_underwriter_id
        );

        setPipeline(myPipeline);
        setUnassigned(unassignedApps);
        setLoading(false);
        return;
      }

      // Transform data for display
      const transformedApps = (apps || []).map(app => ({
        id: app.id,
        anonymous_id: app.anonymous_id || app.id.slice(0, 8),
        applicant: {
          first_name: app.borrower_info?.name?.split(' ')[0] || 'Unknown',
          last_name: app.borrower_info?.name?.split(' ').slice(1).join(' ') || '',
          email: app.borrower_info?.email || '',
          is_test: app.borrower_info?.is_test || false,
        },
        selected_product: {
          name: app.selected_product_data?.name || app.recommended_product?.name || app.product_type || 'Conventional',
          short_name: app.selected_product_data?.short_name || app.recommended_product?.short_name || app.product_type?.split(' ')[0] || 'Conv',
        },
        loan_amount: parseFloat(app.loan_amount) || 0,
        readiness_score: app.readiness_score || 0,
        status: app.status,
        submitted_at: app.submitted_at || app.created_at,
        property_type: app.property_type || 'Single Family',
        credit_score: app.credit_score || 0,
        dti_ratio: app.income_monthly ? ((app.loan_amount * 0.006) / app.income_monthly * 100).toFixed(1) : 0,
        assigned_underwriter_id: app.assigned_underwriter_id,
      }));

      // Split into assigned (my pipeline) and unassigned
      // For demo, treat apps with assigned_underwriter_id as "mine"
      const myPipeline = transformedApps.filter(app => 
        app.status === 'underwriting_in_progress' || 
        app.status === 'conditionally_cleared' ||
        app.status === 'cleared_for_marketplace'
      ).slice(0, 10); // Limit for demo

      const unassignedApps = transformedApps.filter(app => 
        app.status === 'submitted_for_underwriting' && !app.assigned_underwriter_id
      );

      setPipeline(myPipeline);
      setUnassigned(unassignedApps);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await authService.signOut();
    navigate('/uw/login');
  };

  const handleViewApplication = (id) => {
    navigate(`/uw/application/${id}`);
  };

  const handleClaimApplication = async (id) => {
    // Check capacity
    if (pipeline.length >= UNDERWRITER_CONFIG.capacity) {
      alert(`You have reached your capacity of ${UNDERWRITER_CONFIG.capacity} loans. Complete some loans before claiming more.`);
      return;
    }

    const app = unassigned.find((a) => a.id === id);
    if (app) {
      // Update in Supabase
      try {
        const { error } = await supabase
          .from('loan_applications')
          .update({ 
            status: 'underwriting_in_progress',
            // assigned_underwriter_id would be set to current user's ID in real implementation
          })
          .eq('id', id);

        if (error) throw error;

        // Update local state
        setUnassigned(unassigned.filter((a) => a.id !== id));
        setPipeline([...pipeline, { ...app, status: 'underwriting_in_progress' }]);
      } catch (err) {
        console.error('Error claiming application:', err);
        alert('Failed to claim application: ' + err.message);
      }
    }
  };

  const getBorrowerName = (app) => {
    const name = `${app.applicant.first_name} ${app.applicant.last_name}`.trim();
    return name || 'Unknown';
  };

  const filteredPipeline = pipeline.filter(
    (app) =>
      app.anonymous_id?.toLowerCase().includes(search.toLowerCase()) ||
      getBorrowerName(app).toLowerCase().includes(search.toLowerCase())
  );

  const filteredUnassigned = unassigned.filter(
    (app) =>
      app.anonymous_id?.toLowerCase().includes(search.toLowerCase()) ||
      getBorrowerName(app).toLowerCase().includes(search.toLowerCase())
  );

  const capacityUsed = (pipeline.length / UNDERWRITER_CONFIG.capacity) * 100;
  const canClaimMore = pipeline.length < UNDERWRITER_CONFIG.capacity;

  return (
    <div>
      {/* Capacity Warning */}
      {!canClaimMore && (
        <Alert variant="warning" icon={AlertTriangle} style={{ marginBottom: 'var(--lk-space-3)' }}>
          You have reached your maximum capacity of {UNDERWRITER_CONFIG.capacity} loans. Complete some loans before claiming more.
        </Alert>
      )}

      {loading && (
        <div style={{ height: 4, background: 'var(--lk-primary-500)', animation: 'lk-progress 1.5s infinite', marginBottom: 'var(--lk-space-2)' }} />
      )}
      {error && <Alert variant="error" style={{ marginBottom: 'var(--lk-space-2)' }}>{error}</Alert>}

      {/* Stats */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="My Pipeline" value={pipeline.length} />
        <StatCard label="Pending Review" value={pipeline.filter((a) => a.status === 'submitted_for_underwriting').length} />
        <StatCard label="In Progress" value={pipeline.filter((a) => a.status === 'underwriting_in_progress').length} />
        <StatCard label="Unassigned Queue" value={unassigned.length} />
      </Grid>

      {/* Tabs */}
      <Surface padding="0">
        <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5) 0' }}>
          <Tabs
            tabs={[
              { id: 0, label: `My Pipeline (${pipeline.length})` },
              { id: 1, label: `Unassigned (${unassigned.length})` },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        </div>

        <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5) var(--lk-space-5)' }}>
          <div style={{ display: 'flex', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
            <div style={{ width: 300 }}>
              <Input
                placeholder="Search applications..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={fetchApplications} loading={loading}>
              Refresh
            </Button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Borrower</th>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>Loan Amount</th>
                  <th style={{ textAlign: 'center' }}>Credit</th>
                  <th style={{ textAlign: 'center' }}>DTI</th>
                  <th style={{ textAlign: 'center' }}>Score</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(tab === 0 ? filteredPipeline : filteredUnassigned).map((app) => (
                  <tr key={app.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {app.anonymous_id}
                        {app.applicant?.is_test && (
                          <Badge variant="warning" style={{ marginLeft: 4, fontSize: '0.6rem' }}>T</Badge>
                        )}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{getBorrowerName(app)}</div>
                      <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{app.applicant?.email || '—'}</div>
                    </td>
                    <td>
                      <Badge variant="neutral">{app.selected_product.short_name}</Badge>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      ${app.loan_amount.toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>
                      {app.credit_score}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {app.dti_ratio}%
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <TrendingUp size={14} />
                        <Badge variant={getScoreColor(app.readiness_score)}>
                          {app.readiness_score}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <Badge variant={getStatusColor(app.status)}>
                        {getStatusLabel(app.status)}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>
                      {new Date(app.submitted_at).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewApplication(app.id)} />
                        {tab === 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={FileCheck}
                            onClick={() => handleClaimApplication(app.id)}
                            disabled={!canClaimMore}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {(tab === 0 ? filteredPipeline : filteredUnassigned).length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: 'var(--lk-space-8)', color: 'var(--lk-text-quaternary)' }}>
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Surface>

      <FloatingOwenAssistant 
        context="underwriter-dashboard"
        initialMessage={`Welcome back, ${UNDERWRITER_CONFIG.name}! You have ${pipeline.length} applications in your pipeline and ${unassigned.length} in the unassigned queue.`}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Underwriting',
          title: 'Underwriting best practices',
          body: 'Review applications thoroughly and maintain consistent standards across all loans.',
          bullets: [
            'Verify all income and asset documentation',
            'Check credit report for accuracy',
            'Calculate DTI ratios carefully',
            'Review property appraisal details',
            'Document all conditions clearly',
          ],
          resources: [
            { label: 'Underwriting guidelines', href: '#' },
            { label: 'Common conditions', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default UnderwriterDashboard;
